import { useEffect, useRef } from "react";
import { Box } from "@mui/material";

const INK = "#12100d";

/* 玄関背景E — 蛍の杜: 杉並木の影・奥へ続く参道の微光・明滅して漂う蛍 */
export default function ApproachE() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0, h = 0, raf = 0;

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // 蛍（漂いながら、はっきり明滅する）
    const N = 26;
    const flies = Array.from({ length: N }, () => ({
      x: Math.random(),
      y: 0.3 + Math.random() * 0.65, // 下寄り
      vx: (Math.random() - 0.5) * 0.012,
      vy: (Math.random() - 0.5) * 0.006,
      r: 0.9 + Math.random() * 1.3,
      blink: 0.35 + Math.random() * 0.75, // 明滅周期（ゆっくり）
      phase: Math.random() * Math.PI * 2,
      a: 0.35 + Math.random() * 0.45,
    }));

    let last = performance.now();
    const draw = (now) => {
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      const t = now / 1000;
      ctx.clearRect(0, 0, w, h);
      for (const f of flies) {
        if (!reduced) {
          f.x += f.vx * dt;
          f.y += f.vy * dt;
          // ゆるやかに向きを変える
          f.vx += (Math.random() - 0.5) * 0.002 * dt * 60;
          f.vy += (Math.random() - 0.5) * 0.001 * dt * 60;
          f.vx = Math.max(-0.02, Math.min(0.02, f.vx));
          f.vy = Math.max(-0.01, Math.min(0.01, f.vy));
          if (f.x < -0.05) f.x = 1.05;
          if (f.x > 1.05) f.x = -0.05;
          if (f.y < 0.2) { f.y = 0.2; f.vy = Math.abs(f.vy); }
          if (f.y > 1.02) { f.y = 1.02; f.vy = -Math.abs(f.vy); }
        }
        // 蛍らしい「ふわ…ふわ…」の明滅（sinを尖らせる）
        const s = Math.sin(t * f.blink * 2 + f.phase);
        const glow = Math.pow(Math.max(0, s), 2.6);
        const alpha = f.a * (0.08 + 0.92 * (reduced ? 0.6 : glow));
        const x = f.x * w;
        const y = f.y * h + Math.sin(t * 0.7 + f.phase) * 6;
        const g = ctx.createRadialGradient(x, y, 0, x, y, f.r * 7);
        g.addColorStop(0, `rgba(216,190,120,${alpha})`);
        g.addColorStop(0.4, `rgba(212,180,110,${alpha * 0.5})`);
        g.addColorStop(1, "rgba(212,180,110,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, f.r * 7, 0, Math.PI * 2);
        ctx.fill();
      }
      if (!reduced) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: -1, overflow: "hidden", bgcolor: INK }}>
      {/* 杜の基調（中央奥がわずかに明るい） */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse 55% 70% at 50% 62%, #241f19 0%, #17140f 55%, ${INK} 100%)`,
        }}
      />

      {/* 奥へ続く参道の微光 */}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          bottom: 0,
          transform: "translateX(-50%)",
          width: "34vw",
          minWidth: 220,
          height: "58vh",
          background:
            "linear-gradient(180deg, rgba(212,180,110,0) 0%, rgba(212,180,110,0.03) 45%, rgba(222,215,198,0.05) 100%)",
          clipPath: "polygon(46% 0, 54% 0, 78% 100%, 22% 100%)",
          filter: "blur(6px)",
          animation: "kodoPathGlow 9s ease-in-out infinite alternate",
          "@keyframes kodoPathGlow": {
            "0%": { opacity: 0.7 },
            "100%": { opacity: 1 },
          },
        }}
      />

      {/* 杉並木の影（左右・二層） */}
      <Box
        component="svg"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", filter: "blur(2px)" }}
      >
        <g fill="rgba(10,9,7,0.8)">
          <path d="M0,0 L14,0 Q10,18 13,38 Q16,60 11,80 Q9,92 12,100 L0,100 Z" />
          <path d="M100,0 L86,0 Q90,20 87,42 Q84,64 89,82 Q91,93 88,100 L100,100 Z" />
        </g>
        <g fill="rgba(16,14,11,0.65)">
          <path d="M14,0 L24,0 Q20,22 23,48 Q25,72 21,100 L12,100 Q9,92 11,80 Q16,60 13,38 Q10,18 14,0 Z" />
          <path d="M86,0 L76,0 Q80,24 77,50 Q75,74 79,100 L88,100 Q91,93 89,82 Q84,64 87,42 Q90,20 86,0 Z" />
        </g>
      </Box>

      {/* 地表の霧 */}
      <Box
        sx={{
          position: "absolute",
          left: "-20%",
          bottom: "-10%",
          width: "140%",
          height: "34%",
          background: "radial-gradient(ellipse 55% 60% at 50% 70%, rgba(222,215,198,0.045), rgba(222,215,198,0) 70%)",
          filter: "blur(9px)",
          animation: "kodoMoriMist 52s ease-in-out infinite alternate",
          "@keyframes kodoMoriMist": {
            "0%": { transform: "translateX(-6%)" },
            "100%": { transform: "translateX(10%)" },
          },
        }}
      />

      {/* 蛍（canvas） */}
      <Box component="canvas" ref={canvasRef} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />

      {/* 手前の暗がり（文字の可読性） */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse 120% 90% at 50% 115%, rgba(0,0,0,0.5), rgba(0,0,0,0) 55%)",
        }}
      />
    </Box>
  );
}
